import { useState } from 'react';
import { UserPlus, ShieldCheck, RefreshCw, Pencil, UserX, AlertCircle, Clock } from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { BotaoFlutuante } from '@/components/layout/BotaoFlutuante';
import { Button } from '@/components/ui/button';
import { Modal } from '@/components/common/Modal';
import { cn } from '@/lib/utils';
import { getAvatarColor, getUserInitials } from '@/components/common/avatarHelpers';
import { useAuth } from '@/hooks/useAuth';
import {
  useTeam,
  useTeamRoles,
  useCreateTeamMember,
  useUpdateTeamMember,
  useDeactivateTeamMember,
} from '@/hooks/useTeam';
import type { TeamMember } from '@/api/endpoints/team';

const inputClass =
  'w-full rounded-lg border border-border-strong bg-muted px-3 py-2 text-sm text-text-title placeholder:text-text-hint transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30';
const labelClass = 'mb-1.5 block text-sm font-medium text-text-title';

interface FormState {
  id: number | null;
  name: string;
  email: string;
  password: string;
  roles: string[];
  is_active: boolean;
}

const EMPTY: FormState = {
  id: null,
  name: '',
  email: '',
  password: '',
  roles: [],
  is_active: true,
};

export function Team() {
  const { manager } = useAuth();
  const { data: members, isLoading } = useTeam();
  const { data: roleOptions } = useTeamRoles();
  const createM = useCreateTeamMember();
  const updateM = useUpdateTeamMember();
  const deactivateM = useDeactivateTeamMember();

  const [form, setForm] = useState<FormState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saving = createM.isPending || updateM.isPending;

  const roleLabel = (key: string) => roleOptions?.find((r) => r.key === key)?.label ?? key;

  const openCreate = () => {
    setError(null);
    setForm({ ...EMPTY });
  };
  const openEdit = (m: TeamMember) => {
    setError(null);
    setForm({
      id: m.id,
      name: m.name,
      email: m.email,
      password: '',
      roles: m.roles,
      is_active: m.is_active,
    });
  };

  const toggleRole = (key: string) => {
    if (!form) return;
    setForm({
      ...form,
      roles: form.roles.includes(key) ? form.roles.filter((r) => r !== key) : [...form.roles, key],
    });
  };

  const submit = async () => {
    if (!form) return;
    setError(null);
    if (form.name.trim().length < 3) return setError('Nome inválido (mín. 3 caracteres).');
    if (form.roles.length === 0) return setError('Selecione ao menos um cargo.');
    if (!form.id) {
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return setError('E-mail inválido.');
      if (form.password.length < 8) return setError('Senha deve ter no mínimo 8 caracteres.');
    }
    try {
      if (form.id) {
        await updateM.mutateAsync({
          id: form.id,
          data: { name: form.name.trim(), roles: form.roles, is_active: form.is_active },
        });
      } else {
        await createM.mutateAsync({
          name: form.name.trim(),
          email: form.email.trim(),
          password: form.password,
          roles: form.roles,
        });
      }
      setForm(null);
    } catch (err) {
      const fallback = 'Erro ao salvar. Tente novamente.';
      let msg = fallback;
      if (typeof err === 'object' && err !== null) {
        const maybe = err as { response?: { data?: { message?: string } } };
        msg = maybe.response?.data?.message ?? fallback;
      }
      setError(msg);
    }
  };

  const handleDeactivate = async (m: TeamMember) => {
    if (!confirm(`Desativar ${m.name}? O acesso ao painel será revogado.`)) return;
    try {
      await deactivateM.mutateAsync(m.id);
    } catch {
      /* toast futuro */
    }
  };

  return (
    <Layout breadcrumbs={[{ label: 'Equipe' }]}>
      <div className="flex flex-col gap-4 p-3 sm:p-4 lg:gap-6 lg:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="max-md:hidden text-2xl font-bold tracking-tight text-text-title sm:text-3xl">
              Equipe
            </h1>
            <p className="text-sm text-text-secondary">
              Managers do painel e seus cargos de acesso
            </p>
          </div>
          <Button onClick={openCreate} className="max-md:hidden active:scale-[0.98]">
            <UserPlus size={18} />
            <span>Novo membro</span>
          </Button>
          <BotaoFlutuante icon={UserPlus} label="Novo membro" onClick={openCreate} />
        </div>

        <div className="w-full overflow-hidden rounded-xl border border-border bg-card shadow-card">
          {isLoading ? (
            <div className="flex items-center gap-2 p-6 text-sm text-text-secondary">
              <RefreshCw size={16} className="animate-spin" /> Carregando…
            </div>
          ) : (
            <div className="divide-y divide-border">
              {(members ?? []).map((m) => {
                const isSelf = manager?.id === m.id;
                return (
                  <div
                    key={m.id}
                    className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-4 py-3 sm:px-5 md:flex md:flex-wrap md:gap-3"
                  >
                    <div
                      className={cn(
                        'flex size-10 shrink-0 items-center justify-center rounded-full text-sm font-bold',
                        getAvatarColor(String(m.id))
                      )}
                    >
                      {getUserInitials(m.name)}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-semibold text-text-title">
                        <span className="truncate">{m.name}</span>
                        {isSelf && (
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-text-secondary">
                            você
                          </span>
                        )}
                        {!m.is_active && (
                          <span className="rounded-full bg-error/10 px-2 py-0.5 text-[11px] font-medium text-error">
                            inativo
                          </span>
                        )}
                        {m.must_change_password && m.is_active && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning">
                            <Clock size={11} /> trocar senha
                          </span>
                        )}
                      </p>
                      <p className="truncate text-xs text-text-secondary">{m.email}</p>
                    </div>
                    <div className="col-span-2 col-start-2 row-start-2 flex flex-wrap items-center gap-1.5">
                      {m.roles.map((r) => (
                        <span
                          key={r}
                          className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
                        >
                          <ShieldCheck size={11} />
                          {roleLabel(r)}
                        </span>
                      ))}
                    </div>
                    <div className="col-start-3 row-start-1 flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => openEdit(m)}
                        aria-label="Editar"
                        className="rounded-lg p-2 text-text-secondary transition-colors hover:bg-muted hover:text-text-title"
                      >
                        <Pencil size={16} />
                      </button>
                      {m.is_active && !isSelf && (
                        <button
                          onClick={() => handleDeactivate(m)}
                          aria-label="Desativar"
                          className="rounded-lg p-2 text-text-secondary transition-colors hover:bg-error/10 hover:text-error"
                        >
                          <UserX size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modal criar/editar */}
      <Modal
        isOpen={!!form}
        onClose={() => !saving && setForm(null)}
        title={form?.id ? 'Editar membro' : 'Novo membro'}
        size="md"
      >
        {form && (
          <>
            <div className="space-y-4 p-6">
              {error && (
                <div className="flex items-start gap-2 rounded-lg border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">
                  <AlertCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <div>
                <label className={labelClass}>Nome *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className={inputClass}
                  placeholder="Nome completo"
                />
              </div>
              {!form.id && (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className={labelClass}>E-mail *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      className={inputClass}
                      placeholder="nome@curraltech.com"
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Senha inicial *</label>
                    <input
                      type="password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      className={inputClass}
                      placeholder="Mín. 8 caracteres"
                    />
                  </div>
                </div>
              )}
              <div>
                <label className={labelClass}>Cargos *</label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(roleOptions ?? []).map((r) => {
                    const active = form.roles.includes(r.key);
                    return (
                      <button
                        key={r.key}
                        type="button"
                        onClick={() => toggleRole(r.key)}
                        className={cn(
                          'flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                          active
                            ? 'border-primary bg-primary/10 text-text-title'
                            : 'border-border-strong bg-muted text-text-secondary hover:border-primary/50'
                        )}
                      >
                        <span
                          className={cn(
                            'flex size-4 shrink-0 items-center justify-center rounded border',
                            active ? 'border-primary bg-primary text-white' : 'border-text-hint'
                          )}
                        >
                          {active && <ShieldCheck size={11} />}
                        </span>
                        {r.label}
                      </button>
                    );
                  })}
                </div>
              </div>
              {form.id && (
                <label className="flex items-center gap-2 text-sm text-text-title">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                    className="size-4 rounded border-border-strong"
                  />
                  Ativo
                </label>
              )}
            </div>
            <div className="flex justify-end gap-3 border-t border-border bg-bg-elevated p-6">
              <Button variant="secondary" onClick={() => setForm(null)} disabled={saving}>
                Cancelar
              </Button>
              <Button onClick={submit} disabled={saving}>
                {saving && <RefreshCw size={16} className="animate-spin" />}
                {saving ? 'Salvando...' : form.id ? 'Salvar' : 'Criar membro'}
              </Button>
            </div>
          </>
        )}
      </Modal>
    </Layout>
  );
}
